import { Box, Icon, Tooltip, Typography } from '@mui/material'
import { observer } from 'mobx-react-lite'
import { AppBox } from 'src/component/common/analog/molecule/AppBox'
import { AppPaper } from 'src/component/common/analog/molecule/AppPaper'
import { AppSkeleton } from 'src/component/common/analog/molecule/AppSkelton'
import { Palette } from 'src/feature/theme/model/Palette'
import { generateUniqId } from 'src/model/Id'
import { iconMapping } from 'src/util/icon-mapping'

export type PageMetricsProps = {
  sx: any
  metrics: {
    label: string
    value: number | string
    valueColor?: boolean | string
    icon?: string
    iconColor?: string
    tootlTipDesc?: string
  }[]
  backgroundColor?: string,
  isLoading: boolean
  size?: 'small' | 'medium' | 'large'
}

export const PageMetrics = observer(({ sx, metrics, backgroundColor, isLoading, size = 'small' }: PageMetricsProps) => {
  const sizeConfig = {
    small: { fontSize: 12, fontWeight: 400 },
    medium: { fontSize: 14, fontWeight: 500 },
    large: { fontSize: 16, fontWeight: 600 },
  }

  const { fontSize, fontWeight } = sizeConfig[size]

  const getColor = (value: number | string) => {
    if (typeof value === 'number') {
      return value >= 50 ? Palette.OLIVE_GREEN : Palette.DANGER
    }

    if (typeof value === 'string' && value.endsWith('%')) {
      const numericValue = parseFloat(value.slice(0, -1))

      return numericValue >= 50 ? Palette.OLIVE_GREEN : Palette.DANGER
    }

    return 'text.primary'
  }

  return (
    <Box sx={sx}>
      {metrics.map(metric => (
        <AppPaper key={generateUniqId()} sx={{ ...styles.box, backgroundColor, ...(backgroundColor && { border: 0 }) }}>
          <AppBox gap={5} alignItems='center' sx={{ minWidth: 0, width: '100%' }}>
            {metric.tootlTipDesc && (
              <Tooltip title={metric.tootlTipDesc} placement='top' arrow>
                <Icon sx={{ height: 15, width: 15, color: 'secondary.main', cursor: 'help', flexShrink: 0 }} component={iconMapping['InfoIcon']} />
              </Tooltip>
            )}
            <Tooltip title={metric.label} placement='top' arrow>
              <Typography
                noWrap
                sx={{
                  color: 'text.secondary',
                  fontSize: 11,
                  flex: 1,
                  minWidth: 0,
                  textAlign: 'center',
                }}
              >
                {metric.label}
              </Typography>
            </Tooltip>
            {metric.icon && metric.iconColor && (
              <Icon sx={{ height: 15, width: 15, color: metric.iconColor, flexShrink: 0 }} component={iconMapping[metric.icon]} />
            )}
          </AppBox>
          {!isLoading ? (
            <Tooltip title={metric.value} placement="top" arrow>
              <Typography
                noWrap
                sx={{
                  fontSize,
                  fontWeight,
                  color: typeof metric.valueColor === 'string' ? metric.valueColor : metric.valueColor ? getColor(metric.value) : 'text.primary',
                  width: '100%',
                  textAlign: 'center',
                }}
              >
                {metric.value}
              </Typography>
            </Tooltip>
          ) : (
            <AppSkeleton count={1} direction='column' rects={[{ width: '100%', height: 18 }]} />
          )}
        </AppPaper>
      ))}
    </Box>
  )
})

const styles = {
  box: {
    flexDirection: 'column',
    borderRadius: '10px',
    padding: '10px 15px',
    gap: 5,
    minWidth: 0,
  },
}
